import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { EditIcon } from 'src/v3/core/icons'
import CheckIcon from '@mui/icons-material/Check'
import { ClearIcon } from '@mui/x-date-pickers'
import { useFormik } from 'formik'
import { useState } from 'react'
import {
  errorMessageChanged,
  fetchLinkedServices,
  selectActiveLinkedService,
  selectLinkedServiceById,
} from '../manage.slice'

export default () => {
  let dispatch = useAppDispatch()
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedService = useAppSelector(
    selectLinkedServiceById(activeLinkedService),
  )
  let [
    updatingAssignmentCompletionThreshold,
    setUpdatingAssignmentCompletionThreshold,
  ] = useState<boolean>(false)
  let [
    editingAssignmentCompletionThreshold,
    setEditingAssignmentCompletionThreshold,
  ] = useState<boolean>(false)
  let [updatingScheduledThreshold, setUpdatingScheduledThreshold] =
    useState<boolean>(false)
  let [showScheduledThresholdDialog, setShowScheduledThresholdDialog] =
    useState<boolean>(false)

  let hasThresholdSet = !!linkedService?.completionThreshold

  let form = useFormik({
    validationSchema: yup.object({
      assignmentCompletionThreshold: yup.number().integer().min(0).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      assignmentCompletionThreshold: linkedService?.completionThreshold || 0,
    },
    onSubmit: (values) => {
      setUpdatingAssignmentCompletionThreshold(true)
      let shouldDisableScheduledThreshold =
        !values.assignmentCompletionThreshold &&
        linkedService?.applyThresholdToScheduledAssignments

      let thresholdRequest = axios.put('/api/manage/linked-service/threshold', {
        id: activeLinkedService,
        completionThreshold: values.assignmentCompletionThreshold,
      })

      let request = shouldDisableScheduledThreshold
        ? thresholdRequest.then(() =>
            axios.put(
              `/api/manage/linked-service/${activeLinkedService}/scheduled-assignment-threshold`,
              { applyThresholdToScheduledAssignments: false },
            ),
          )
        : thresholdRequest

      request
        .then(() => dispatch(fetchLinkedServices()))
        .then(() => setUpdatingAssignmentCompletionThreshold(false))
        .then(() => form.resetForm())
        .catch((err) => {
          dispatch(
            errorMessageChanged(
              err?.message || 'Error Updating Assignment Completion Threshold',
            ),
          )
          dispatch(fetchLinkedServices())
          setUpdatingAssignmentCompletionThreshold(false)
        })
    },
  })

  let handleScheduledThresholdChange = (checked: boolean) => {
    setUpdatingScheduledThreshold(true)
    axios
      .put(
        `/api/manage/linked-service/${activeLinkedService}/scheduled-assignment-threshold`,
        { applyThresholdToScheduledAssignments: checked },
      )
      .then(() => dispatch(fetchLinkedServices()))
      .then(() => setUpdatingScheduledThreshold(false))
      .catch((err) => {
        dispatch(
          errorMessageChanged(
            err?.message ||
              'Error Updating Scheduled Assignment Threshold Setting',
          ),
        )
        dispatch(fetchLinkedServices())
        setUpdatingScheduledThreshold(false)
      })
  }

  return (
    <Box>
      {editingAssignmentCompletionThreshold ? (
        <form onSubmit={form.handleSubmit}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              label="Threshold (months)"
              variant="outlined"
              size="small"
              name="assignmentCompletionThreshold"
              type="number"
              fullWidth
              value={form.values.assignmentCompletionThreshold}
              onChange={form.handleChange}
              error={
                form.touched.assignmentCompletionThreshold &&
                !!form.errors.assignmentCompletionThreshold
              }
              sx={{ flex: 1 }}
            />
            <Button
              color="error"
              size="small"
              onClick={() => {
                setEditingAssignmentCompletionThreshold(false)
                form.resetForm()
              }}
              disabled={updatingAssignmentCompletionThreshold}
            >
              <ClearIcon />
            </Button>
            <Button
              color="success"
              size="small"
              type="submit"
              disabled={updatingAssignmentCompletionThreshold}
            >
              {updatingAssignmentCompletionThreshold ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckIcon />
              )}
            </Button>
          </Box>
        </form>
      ) : (
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            Completion Threshold
          </Typography>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {linkedService?.completionThreshold
              ? `${linkedService?.completionThreshold} months`
              : 'No threshold set'}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setEditingAssignmentCompletionThreshold(true)}
            sx={{ textTransform: 'none' }}
            disabled={!linkedService}
          >
            Edit Threshold
          </Button>
        </Box>
      )}
      {hasThresholdSet && (
        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={
                  linkedService?.applyThresholdToScheduledAssignments ?? false
                }
                onChange={(_, checked) => {
                  if (checked) {
                    setShowScheduledThresholdDialog(true)
                  } else {
                    handleScheduledThresholdChange(false)
                  }
                }}
                disabled={updatingScheduledThreshold}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Apply assignment thresholds to scheduled assignments
                </Typography>
                {updatingScheduledThreshold && <CircularProgress size={14} />}
              </Box>
            }
          />
        </Box>
      )}
      <Dialog
        open={showScheduledThresholdDialog}
        onClose={() => setShowScheduledThresholdDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Enable Scheduled Assignment Thresholds?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            When this setting is on, the completion threshold will be
            automatically applied to <strong>all</strong> users who meet it at
            the time a scheduled assignment is created — no manager review
            required.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This is different from manually created assignments, where the
            manager is shown a list of eligible users and can choose whether to
            grant credit selectively.
          </Typography>
          <Typography variant="body2">
            Because scheduled assignments are created by an automated process,
            there is no opportunity for selective review. Any user whose prior
            course completion falls within the threshold window will receive
            credit automatically and will not be enrolled in the assignment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowScheduledThresholdDialog(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowScheduledThresholdDialog(false)
              handleScheduledThresholdChange(true)
            }}
            variant="contained"
            color="warning"
          >
            Enable Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
